import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import EnLayout from '@/components/feature/EnLayout';
import useSEO from '@/hooks/useSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import RelatedPages from '@/components/base/RelatedPages';

// ─── Core Page ───────────────────────────────────────────────────────────────

const corePrinciples = [
  {
    icon: 'ri-leaf-line',
    title: 'Nature-First Selection',
    desc: 'Every ingredient comes with full traceability documentation and meets food safety standards — quality assurance begins at the source.',
    color: '#4CAF50',
  },
  {
    icon: 'ri-eye-line',
    title: 'Transparency & Trust',
    desc: 'From ingredient selection and formula design to the production process — everything is open and transparent, building genuine trust with clients and consumers.',
    color: '#43a047',
  },
  {
    icon: 'ri-plant-line',
    title: 'Long-Term Stability',
    desc: 'We do not chase exaggerated claims. We are committed to empowering the body to maintain stable health in daily life, supporting brands for the long run.',
    color: '#388e3c',
  },
];

const coreBeliefs = [
  {
    num: '01',
    title: 'Return to Nature',
    desc: 'The nutrients that truly coexist with the human body must return to their natural essence. We believe the purest power comes from the earth.',
  },
  {
    num: '02',
    title: 'Quality Starts with Ingredients',
    desc: 'We carefully select traceable, food-safety-compliant natural materials, so every ounce of trust is built on a transparent and reliable foundation.',
  },
  {
    num: '03',
    title: 'Consultative Partnership',
    desc: 'Kang Yong Biotech acts as a professional consultant, helping brands build products that stand up to market scrutiny and regulatory review.',
  },
];

function CorePage() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[380px] md:min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=serene%20natural%20landscape%20with%20lush%20green%20forest%20misty%20mountains%20at%20dawn%2C%20pure%20clean%20water%20stream%20flowing%20through%20rocks%2C%20tranquil%20zen%20atmosphere%2C%20soft%20natural%20morning%20light%2C%20botanical%20wellness%20philosophy%20background&width=1920&height=440&seq=core-hero-bg-01&orientation=landscape"
            alt="Core Philosophy Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2310]/88 via-[#0d2310]/60 to-[#0d2310]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[{ label: 'Home', href: '/en' }, { label: 'Core Philosophy' }]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              Home · Core Philosophy
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              Core Philosophy
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              Nature is the foundation of lasting health.
            </p>
          </div>
        </div>
      </section>

      {/* Quote band */}
      <section className="bg-[#4CAF50] py-6">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10">
          {[
            { icon: 'ri-leaf-line', text: 'Strict Natural Ingredient Traceability' },
            { icon: 'ri-shield-check-line', text: 'Full Food Safety Compliance' },
            { icon: 'ri-heart-line', text: 'Protecting Consumer Wellbeing' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-white text-xl`} />
              </div>
              <span className="text-white font-semibold text-[1rem]">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Brand belief intro */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative rounded-2xl overflow-hidden" style={{ height: '440px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=close-up%20of%20natural%20herbal%20ingredients%20arranged%20beautifully%20on%20clean%20white%20surface%2C%20various%20botanical%20roots%20seeds%20leaves%20flowers%2C%20health%20supplement%20ingredients%20flat%20lay%2C%20warm%20natural%20lighting%20minimal%20style&width=800&height=440&seq=core-intro-img-01&orientation=landscape"
                alt="Kang Yong Biotech Core Philosophy - Natural Ingredients"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-6 left-6 bg-white/95 rounded-xl px-5 py-3 border border-green-100">
                <span className="text-[#4CAF50] font-bold text-[0.9375rem]">20+ Years of Natural Ingredient Research</span>
              </div>
            </div>
            <div>
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                Brand Philosophy
              </span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-6">
                Return to Nature — The Right Foundation
              </h2>
              <div className="space-y-4 text-gray-500 text-[1.0625rem] leading-[1.85]">
                <p>
                  Kang Yong Biotech believes that the nutrients that truly coexist with the human body must return to their natural essence. We insist on starting from the source — selecting traceable, food-safety-compliant natural materials so that every ounce of trust is built on a transparent and reliable foundation.
                </p>
                <p>
                  Natural materials each have their own value, and the responsibility of health supplements is not to chase exaggerated effects, but to empower the body to maintain stability in daily life. Kang Yong acts as a professional consultant, walking alongside brands to build products that stand up to both market scrutiny and regulatory review.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three principles cards */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Three Core Principles
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              What We Stand By in Everything We Do
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              From ingredient sourcing and production to our approach to partnerships — three core principles guide every aspect of Kang Yong Biotech
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {corePrinciples.map((p) => (
              <div
                key={p.title}
                className="bg-white rounded-2xl p-8 border border-gray-100 hover:-translate-y-1 hover:border-green-200 transition-all duration-300"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-green-50 border border-green-100 mb-6">
                  <i className={`${p.icon} text-[#4CAF50] text-2xl`} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1.1875rem] mb-3">{p.title}</h3>
                <p className="text-gray-500 text-[0.9375rem] leading-[1.8]">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Belief steps */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                Our Beliefs
              </span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-8">
                Protecting Lasting Health Together with Our Clients
              </h2>
              <div className="space-y-7">
                {coreBeliefs.map((b) => (
                  <div key={b.num} className="flex gap-5">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-green-50 rounded-full border border-green-200">
                      <span className="text-[#4CAF50] font-bold text-[0.875rem]">{b.num}</span>
                    </div>
                    <div>
                      <h4 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{b.title}</h4>
                      <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden" style={{ height: '420px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=professional%20biotech%20laboratory%20researcher%20examining%20natural%20plant%20extract%20samples%20under%20microscope%2C%20clean%20white%20lab%20environment%20with%20green%20plants%20visible%2C%20health%20supplement%20quality%20control%20process%2C%20warm%20lighting&width=800&height=420&seq=core-lab-img-01&orientation=landscape"
                alt="Kang Yong Research Team"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d2310]/40 to-transparent" />
              <div className="absolute bottom-6 right-6 bg-white/95 rounded-xl px-5 py-3 border border-green-100">
                <span className="text-[#4CAF50] font-bold text-[0.9375rem]">Expert R&D Consultant Team</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            Let&apos;s Build Truly Great Products Together
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            With Kang Yong&apos;s natural ingredient philosophy and consultative service approach, help your brand go further and last longer.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/en/contact/form"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              Inquire About ODM Partnership
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/en/home/advantages"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              Our Advantages
            </Link>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: 'Kang Yong Advantages', desc: 'Learn about our four core advantages in natural ingredient selection, integrated services and flexible MOQ.', href: '/en/home/advantages' },
        { title: 'Cooperation Process', desc: 'Get a clear picture of every step from requirement alignment to final delivery.', href: '/en/cooperation/process' },
        { title: 'Start an Inquiry', desc: 'Fill out our inquiry form and a specialist will get back to you promptly.', href: '/en/contact/form' },
      ]} />
    </>
  );
}

// ─── Advantages Page ──────────────────────────────────────────────────────────

const advantagesData = [
  {
    icon: 'ri-leaf-line',
    num: '01',
    title: 'Commitment to Natural Ingredients',
    desc: 'We strictly select natural food-grade sources and reject unnecessary chemical additives. Every ingredient comes with complete traceability documentation and identity certification, ensuring the quality and safety of your product.',
    highlights: ['Complete Traceability', 'No Chemical Additives', 'Food Safety Compliant', 'Ingredient Identity Certification'],
    imgSrc: 'https://readdy.ai/api/search-image?query=natural%20organic%20botanical%20ingredients%20arranged%20on%20white%20surface%2C%20various%20seeds%20herbs%20roots%20vitamins%20capsules%2C%20clean%20minimal%20food%20photography%2C%20health%20supplement%20raw%20materials%20top%20view&width=700&height=420&seq=adv-img-01&orientation=landscape',
  },
  {
    icon: 'ri-group-line',
    num: '02',
    title: 'Creating a Win-Win-Win Outcome',
    desc: 'Kang Yong does not compete with its clients. We focus on enabling brands to enter the market steadily, helping consumers supplement safely and confidently, and maintaining unwavering quality in manufacturing — creating a positive cycle for all three parties.',
    highlights: ['Stable Brand Entry', 'Consumer Peace of Mind', 'Consistent Manufacturing Quality', 'Tri-Party Win Cycle'],
    imgSrc: 'https://readdy.ai/api/search-image?query=collaborative%20business%20partners%20shaking%20hands%20in%20bright%20modern%20office%2C%20health%20biotech%20startup%20team%20meeting%2C%20positive%20cooperation%20concept%2C%20green%20plants%20in%20background%2C%20natural%20warm%20light&width=700&height=420&seq=adv-img-02&orientation=landscape',
  },
  {
    icon: 'ri-tools-line',
    num: '03',
    title: 'Integrated Professional Services',
    desc: 'We integrate needs analysis, formula design, dosage form selection, brand visuals, regulatory compliance and production into manageable steps — reducing communication costs and minimizing costly market trial-and-error.',
    highlights: ['One-Stop Integration', 'Formula & Dosage Design', 'Regulatory Review', 'Reduced Trial-and-Error Risk'],
    imgSrc: 'https://readdy.ai/api/search-image?query=professional%20team%20analyzing%20health%20supplement%20product%20development%20workflow%20on%20large%20screen%2C%20modern%20biotech%20company%20office%2C%20organized%20project%20planning%20board%2C%20clean%20white%20workspace%20with%20natural%20light&width=700&height=420&seq=adv-img-03&orientation=landscape',
  },
  {
    icon: 'ri-scales-line',
    num: '04',
    title: 'Small Batch Order Accepted',
    desc: 'We offer flexible production options to help new brands and niche market players reduce inventory pressure and capital risk, making it easy to test great nutrition in the market without financial stress.',
    highlights: ['Flexible MOQ', 'New Brand Friendly', 'Reduced Inventory Risk', 'Fast Market Testing'],
    imgSrc: 'https://readdy.ai/api/search-image?query=small%20batch%20health%20supplement%20production%20line%20in%20clean%20white%20GMP%20factory%2C%20neatly%20arranged%20capsule%20bottles%20and%20packaging%2C%20precise%20manufacturing%20process%2C%20minimal%20clinical%20aesthetic&width=700&height=420&seq=adv-img-04&orientation=landscape',
  },
];

const advStats = [
  { value: '250+', label: 'Brand Partners' },
  { value: '1600+', label: 'Formula Database' },
  { value: '70%', label: 'Repeat Order Rate' },
  { value: '7', label: 'Dosage Form Types' },
];

function AdvantagesPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[380px] md:min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20biotech%20health%20supplement%20company%20building%20exterior%20with%20green%20living%20wall%20plants%2C%20professional%20corporate%20headquarters%20surrounded%20by%20nature%2C%20clean%20architecture%20daylight&width=1920&height=440&seq=adv-hero-bg-01&orientation=landscape"
            alt="Kang Yong Advantages Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2310]/90 via-[#0d2310]/60 to-[#0d2310]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[{ label: 'Home', href: '/en' }, { label: 'Kang Yong Advantages' }]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              Home · Kang Yong Advantages
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              Kang Yong Advantages
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              From natural ingredient sourcing and brand collaboration to production integration and flexible MOQ — lowering entry barriers and market risks for brands.
            </p>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-white border-b border-gray-100 py-8">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {advStats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-[#4CAF50] font-bold text-[2rem] md:text-[2.25rem] leading-none mb-1">{s.value}</div>
                <div className="text-gray-500 text-[0.875rem]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages alternating image-text */}
      <section className="py-6 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 space-y-6 py-10">
          {advantagesData.map((adv, idx) => (
            <div
              key={adv.num}
              className={`bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
            >
              <div className="lg:w-[44%] flex-shrink-0" style={{ minHeight: '300px' }}>
                <img
                  src={adv.imgSrc}
                  alt={adv.title}
                  className="w-full h-full object-cover object-top"
                  style={{ minHeight: '260px' }}
                />
              </div>
              <div className="flex-1 p-8 lg:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-green-50 border border-green-100">
                    <i className={`${adv.icon} text-[#4CAF50] text-xl`} />
                  </div>
                  <span className="text-[#4CAF50] font-bold text-[1.5rem] opacity-30">{adv.num}</span>
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1.5rem] md:text-[1.75rem] mb-4">{adv.title}</h3>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.85] mb-6">{adv.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {adv.highlights.map((h) => (
                    <span key={h} className="inline-flex items-center gap-1.5 text-[0.8125rem] text-[#4CAF50] bg-green-50 border border-green-100 px-3 py-1 rounded-full">
                      <i className="ri-check-line text-xs" />
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
            Why Choose Kang Yong
          </span>
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
            More Than a Factory — Your Biotech Growth Partner
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto mb-12">
            The key difference between Kang Yong and a typical contract manufacturer is this: we treat every collaboration as a long-term partnership, not just a completed order.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-user-voice-line', title: 'Dedicated Account Service', desc: 'From the first consultation to post-delivery follow-up, a dedicated account manager handles your project throughout' },
              { icon: 'ri-file-shield-line', title: 'NDA Confidentiality', desc: 'We sign strict non-disclosure agreements to ensure your formula ideas are fully protected' },
              { icon: 'ri-building-2-line', title: 'GMP Facility Production', desc: 'Taichung GMP-compliant facility with rigorous quality inspection for every batch' },
              { icon: 'ri-scales-2-line', title: 'Regulatory Compliance Review', desc: 'Familiar with food and health supplement advertising regulations to ensure compliant market launch' },
              { icon: 'ri-refresh-line', title: 'Fast Response Capability', desc: 'Small batch testing, rapid samples and flexible adjustments to seize market opportunities quickly' },
              { icon: 'ri-bar-chart-line', title: 'Market Trend Insights', desc: 'Stay ahead of health supplement market trends to help brands choose the right direction' },
            ].map((item) => (
              <div key={item.title} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 text-left hover:border-green-200 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl border border-green-100 mb-4">
                  <i className={`${item.icon} text-[#4CAF50] text-xl`} />
                </div>
                <h4 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{item.title}</h4>
                <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            Bring Your Ideas — Leave the Rest to Us
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            Kang Yong Biotech offers a free initial assessment to show you how to launch your first health supplement brand at the lowest possible cost.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/en/cooperation/process"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              Learn About the Cooperation Process
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/en/home/experience"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              Our Service Experience
            </Link>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: 'Core Philosophy', desc: "Kang Yong's brand belief: returning to nature is the right path for health.", href: '/en/home/core' },
        { title: 'Professional Manufacturing', desc: 'Learn about Kang Yong\'s GMP facility and quality control processes.', href: '/en/about/production' },
        { title: 'Inquire About Manufacturing Services', desc: 'Fill out the inquiry form and let Kang Yong evaluate the best collaboration approach for you.', href: '/en/contact/form' },
      ]} />
    </>
  );
}

// ─── Experience Page ──────────────────────────────────────────────────────────

interface CountUpProps {
  target: number;
  suffix?: string;
  duration?: number;
}

function CountUp({ target, suffix = '', duration = 2000 }: CountUpProps) {
  const [count, setCount] = useState(0);
  const hasStarted = useRef(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted.current) {
          hasStarted.current = true;
          const startTime = performance.now();
          const step = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

const milestones = [
  { year: '2003', title: 'Founded Kang Yong Biotech', desc: 'Established by senior food R&D experts, dedicated to natural health supplement manufacturing' },
  { year: '2008', title: 'GMP Facility Certification', desc: 'Officially obtained GMP certification, fully upgrading production standards' },
  { year: '2013', title: 'Surpassed 100 Brand Partners', desc: 'Served over 100 brands, establishing a comprehensive ODM service system' },
  { year: '2018', title: 'Smart Management System Introduced', desc: 'Implemented ERP management system to enhance production tracking and quality control' },
  { year: '2022', title: 'Reached 350+ Brand Partners', desc: 'Cumulative brand partners exceeded 350, spanning e-commerce, pharmacies and chain retailers' },
];

const categoryExperiences = [
  { icon: 'ri-heart-pulse-line', label: 'Beauty & Skincare', count: '80+', desc: 'Radiance, hydration and antioxidant series' },
  { icon: 'ri-basketball-line', label: 'Sports & Fitness', count: '65+', desc: 'Muscle support and energy maintenance series' },
  { icon: 'ri-leaf-line', label: "Women's Health", count: '90+', desc: "Women's wellness and hormonal balance series" },
  { icon: 'ri-mental-health-line', label: 'Sleep & Stress', count: '55+', desc: 'Sleep support and nervous system series' },
  { icon: 'ri-seedling-line', label: 'Gut Health', count: '70+', desc: 'Probiotic and digestive support series' },
  { icon: 'ri-star-line', label: "Children's Growth", count: '40+', desc: "Children's calcium and cognitive support series" },
];

const expTestimonials = [
  {
    name: 'Mr. Wang, Brand Founder',
    company: 'E-commerce Health Brand',
    text: 'Kang Yong supported everything from formula to packaging, helping my brand launch online within 6 months from scratch, saving enormous trial-and-error costs.',
    tag: 'E-commerce Brand',
  },
  {
    name: 'Ms. Li, Director',
    company: 'Chain Pharmacy Purchasing',
    text: 'Having worked with Kang Yong for many years, I especially appreciate their familiarity with regulations — every product they deliver gets us on shelves without any compliance worries.',
    tag: 'Pharmacy Channel',
  },
  {
    name: 'Mr. Chen, CEO',
    company: 'Health Brand Group',
    text: 'The small batch ordering option has been invaluable — no need to hold large inventory during the testing phase. Once a product proves popular, we scale up. Very flexible.',
    tag: 'Multi-Brand Group',
  },
];

function ExperiencePage() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[380px] md:min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=successful%20health%20supplement%20brand%20launch%20celebration%2C%20diverse%20business%20team%20in%20modern%20office%2C%20product%20achievement%20wall%20with%20multiple%20award%20certificates%2C%20green%20accents%20corporate%20environment%2C%20natural%20lighting&width=1920&height=440&seq=exp-hero-bg-01&orientation=landscape"
            alt="Service Experience Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2310]/90 via-[#0d2310]/60 to-[#0d2310]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[{ label: 'Home', href: '/en' }, { label: 'Service Experience' }]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              Home · Service Experience
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              Service Experience
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              With a solid service track record, we walk alongside brands from product concept to market launch.
            </p>
          </div>
        </div>
      </section>

      {/* Key stats with count-up */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Key Metrics
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              The Numbers Behind Every Trust
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              20 years of deep expertise in health supplement manufacturing — our numbers tell the story of our professional commitment
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { target: 250, suffix: '+', label: 'Brand Partners Served', desc: 'Spanning e-commerce, pharmacies, direct sales, chain retail and more', icon: 'ri-store-2-line' },
              { target: 1600, suffix: '+', label: 'Formulas in Database', desc: 'Across beauty, sports, gut health and many other categories', icon: 'ri-flask-line' },
              { target: 70, suffix: '%', label: 'Repeat Order Rate', desc: 'Real trust built on years of consistent quality delivery and word-of-mouth referrals', icon: 'ri-star-smile-line' },
            ].map((stat) => (
              <div key={stat.label} className="bg-[#f7faf7] rounded-2xl p-8 border border-gray-100 text-center hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-2xl border border-green-100 mx-auto mb-5">
                  <i className={`${stat.icon} text-[#4CAF50] text-2xl`} />
                </div>
                <div className="text-[#4CAF50] font-bold text-[3rem] md:text-[3.5rem] leading-none mb-2">
                  <CountUp target={stat.target} suffix={stat.suffix} />
                </div>
                <div className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{stat.label}</div>
                <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{stat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category experience */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Product Category Experience
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              Covering Diverse Health Categories
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              Our extensive cross-category development experience means we have the right solution no matter your market positioning
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {categoryExperiences.map((cat) => (
              <div key={cat.label} className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-green-200 transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 flex items-center justify-center bg-green-50 rounded-xl border border-green-100 flex-shrink-0">
                    <i className={`${cat.icon} text-[#4CAF50] text-xl`} />
                  </div>
                  <div>
                    <div className="text-[#1a2e1a] font-bold text-[1rem]">{cat.label}</div>
                    <div className="text-[#4CAF50] font-bold text-[0.875rem]">{cat.count} Products</div>
                  </div>
                </div>
                <p className="text-gray-500 text-[0.875rem] leading-[1.7]">{cat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Development Milestones
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              Steady Growth
            </h2>
          </div>
          <div className="relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-green-100 -translate-x-1/2" />
            <div className="space-y-8 md:space-y-0">
              {milestones.map((m, idx) => (
                <div key={m.year} className={`flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-0 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`md:w-[calc(50%-2rem)] ${idx % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                    <div className={`bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 transition-all duration-300 ${idx % 2 === 0 ? 'md:ml-auto md:max-w-md' : 'md:mr-auto md:max-w-md'}`}>
                      <div className="text-[#4CAF50] font-bold text-[1.375rem] mb-2">{m.year}</div>
                      <div className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{m.title}</div>
                      <p className="text-gray-500 text-[0.9375rem] leading-[1.7]">{m.desc}</p>
                    </div>
                  </div>
                  <div className="hidden md:flex w-16 flex-shrink-0 items-center justify-center z-10">
                    <div className="w-4 h-4 rounded-full bg-[#4CAF50] border-4 border-white ring-2 ring-green-200" />
                  </div>
                  <div className="md:w-[calc(50%-2rem)]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Brand Voices
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              Real Feedback from Our Brand Partners
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {expTestimonials.map((t) => (
              <div key={t.name} className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-green-200 transition-all duration-300">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg" />
                  ))}
                </div>
                <p className="text-gray-600 text-[0.9375rem] leading-[1.85] mb-6 italic">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[#1a2e1a] font-bold text-[0.9375rem]">{t.name}</div>
                    <div className="text-gray-400 text-[0.8125rem]">{t.company}</div>
                  </div>
                  <span className="text-[0.75rem] text-[#4CAF50] bg-green-50 border border-green-100 px-2.5 py-1 rounded-full">{t.tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            Become Our Next Success Story
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            Whether you&apos;re a first-time brand founder or an established company looking to expand, Kang Yong has the right partnership solution for you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/en/contact/form"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              Inquire About Manufacturing Services
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/en/about/cases"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              View Success Cases
            </Link>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: 'Success Cases', desc: 'See how Kang Yong has helped brands go from concept to market launch.', href: '/en/about/cases' },
        { title: 'Cooperation Process', desc: 'From requirement alignment to final delivery — a clear and controllable partnership process.', href: '/en/cooperation/process' },
        { title: 'Start an Inquiry', desc: 'Fill out the inquiry form and become the next successful Kang Yong partner.', href: '/en/contact/form' },
      ]} />
    </>
  );
}

// ─── Hot Formulas Page ────────────────────────────────────────────────────────

const formulasEn = [
  {
    id: 'crystal-bright',
    icon: 'ri-eye-line',
    title: 'Eye Bright Formula',
    tag: 'Eye Health',
    desc: 'Lutein and zeaxanthin complex targeting those who spend long hours looking at screens, supporting visual health.',
    dosages: ['Soft Gel', 'Tablet', 'Powder'],
    targets: ['Office Workers', 'Students', 'Seniors'],
    color: 'bg-green-50 border-green-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=eye%20health%20supplement%20capsules%20with%20marigold%20flowers%20lutein%20zeaxanthin%20ingredients%20on%20clean%20white%20surface%2C%20minimal%20health%20product%20photography&width=400&height=260&seq=hf-01&orientation=landscape',
  },
  {
    id: 'womens',
    icon: 'ri-women-line',
    title: "Women's Formula",
    tag: "Women's Health",
    desc: "Intimate health, hormonal balance, beauty nourishment — comprehensive support for women's needs at every life stage.",
    dosages: ['Capsule', 'Tablet', 'Jelly Strip'],
    targets: ['Women of Reproductive Age', 'Menopausal Women', 'Beauty-Focused'],
    color: 'bg-rose-50 border-rose-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=womens%20health%20supplement%20products%20with%20rose%20flower%20petals%20and%20botanical%20ingredients%2C%20feminine%20health%20care%20products%20flat%20lay%20on%20white%20background%2C%20natural%20pink%20tones&width=400&height=260&seq=hf-02&orientation=landscape',
  },
  {
    id: 'mens',
    icon: 'ri-men-line',
    title: "Men's Formula",
    tag: "Men's Vitality",
    desc: 'Vitality support, prostate health and physical endurance — formulas designed specifically for modern men.',
    dosages: ['Capsule', 'Tablet', 'Powder'],
    targets: ['Middle-Aged Men', 'Active Lifestyle', 'High Stress Workers'],
    color: 'bg-sky-50 border-sky-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=mens%20health%20supplement%20capsules%20with%20zinc%20selenium%20saw%20palmetto%20botanical%20ingredients%20on%20clean%20white%20surface%2C%20masculine%20health%20product%20flat%20lay&width=400&height=260&seq=hf-03&orientation=landscape',
  },
  {
    id: 'gut',
    icon: 'ri-seedling-line',
    title: 'Gut Health Formula',
    tag: 'Digestive Health',
    desc: 'Multi-strain probiotic complex with dietary fiber to support gut microbiome balance and digestive comfort.',
    dosages: ['Capsule', 'Powder Sachet', 'Tablet'],
    targets: ['All Ages', 'Digestive Issues', 'Low Fiber Diets'],
    color: 'bg-green-50 border-green-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=probiotic%20gut%20health%20supplement%20powder%20capsules%20with%20fermented%20ingredients%20prebiotics%20fiber%20on%20clean%20white%20surface%2C%20health%20food%20photography%20minimal&width=400&height=260&seq=hf-04&orientation=landscape',
  },
  {
    id: 'hydration',
    icon: 'ri-drop-line',
    title: 'Hydration & Beauty Formula',
    tag: 'Beauty & Skincare',
    desc: 'Triple-action complex of hyaluronic acid, collagen and vitamin C to maintain skin hydration and radiance from within.',
    dosages: ['Powder Sachet', 'Jelly Strip', 'Capsule'],
    targets: ['Beauty Enthusiasts', 'Dry Skin Types', 'Mature Skincare'],
    color: 'bg-amber-50 border-amber-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=collagen%20hyaluronic%20acid%20beauty%20supplement%20powder%20sachets%20with%20peach%20and%20rose%20on%20white%20surface%2C%20feminine%20health%20skincare%20product%20photography&width=400&height=260&seq=hf-05&orientation=landscape',
  },
  {
    id: 'hair',
    icon: 'ri-scissors-line',
    title: 'Hair Health Formula',
    tag: 'Hair Care',
    desc: 'Saw palmetto, biotin and cysteine complex for comprehensive care targeting hair loss concerns.',
    dosages: ['Capsule', 'Tablet'],
    targets: ['Hair Loss Concerns', 'Middle-Aged Adults', 'Postpartum Mothers'],
    color: 'bg-orange-50 border-orange-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=hair%20health%20supplement%20biotin%20capsules%20with%20rosemary%20herbs%20saw%20palmetto%20on%20white%20surface%2C%20hair%20care%20product%20minimal%20photography%20warm%20natural%20tones&width=400&height=260&seq=hf-06&orientation=landscape',
  },
  {
    id: 'baby',
    icon: 'ri-parent-line',
    title: 'Baby Formula',
    tag: 'Infant & Toddler',
    desc: 'Formulas specifically designed for infants and toddlers — DHA, probiotics and vitamin D, with gentle additions and strict quality control.',
    dosages: ['Powder', 'Drops'],
    targets: ['Ages 0–3', 'Nursing Mothers', 'Parent Selection'],
    color: 'bg-yellow-50 border-yellow-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=baby%20infant%20supplement%20powder%20formula%20DHA%20omega3%20vitamins%20on%20clean%20white%20surface%2C%20gentle%20soft%20pastel%20tones%20health%20product%20photography%20for%20infants&width=400&height=260&seq=hf-07&orientation=landscape',
  },
  {
    id: 'pets',
    icon: 'ri-heart-line',
    title: 'Pet Health Formula',
    tag: 'Pet Care',
    desc: 'Health formulas designed for cats and dogs — joint care, gut health and coat shine in three key directions.',
    dosages: ['Powder', 'Tablet', 'Treat Form'],
    targets: ['Cat Owners', 'Dog Owners', 'Senior Pets'],
    color: 'bg-amber-50 border-amber-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=pet%20health%20supplement%20products%20for%20dogs%20and%20cats%2C%20natural%20ingredient%20treats%20powder%20on%20white%20surface%2C%20veterinary%20supplement%20product%20minimal%20photography%20warm%20tones&width=400&height=260&seq=hf-08&orientation=landscape',
  },
  {
    id: 'fitness',
    icon: 'ri-run-line',
    title: 'Sports Performance Formula',
    tag: 'Sports & Fitness',
    desc: 'Whey protein, BCAA and creatine complex to support fast post-workout recovery and muscle maintenance.',
    dosages: ['Powder', 'Capsule', 'Tablet'],
    targets: ['Gym Enthusiasts', 'Athletes', 'Active Lifestyle'],
    color: 'bg-emerald-50 border-emerald-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=sports%20fitness%20supplement%20protein%20powder%20capsules%20with%20green%20leaf%20botanical%20ingredients%20on%20clean%20white%20surface%2C%20athletic%20health%20product%20flat%20lay%20photography&width=400&height=260&seq=hf-09&orientation=landscape',
  },
  {
    id: 'sleep',
    icon: 'ri-moon-line',
    title: 'Sleep & Relaxation Formula',
    tag: 'Sleep Support',
    desc: 'Triple-action sleep complex with tryptophan, GABA and L-theanine to help improve sleep onset and quality.',
    dosages: ['Capsule', 'Tablet', 'Jelly Strip'],
    targets: ['Insomnia Sufferers', 'High Stress Individuals', 'Shift Workers'],
    color: 'bg-indigo-50 border-indigo-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=sleep%20wellness%20supplement%20chamomile%20lavender%20valerian%20melatonin%20products%20on%20white%20surface%2C%20calming%20nighttime%20health%20product%20photography%20soft%20purple%20tones&width=400&height=260&seq=hf-10&orientation=landscape',
  },
  {
    id: 'kids',
    icon: 'ri-user-heart-line',
    title: "Children's Growth Formula",
    tag: "Children's Health",
    desc: "Calcium, vitamin D3 and brain-supporting DHA to support children's bone development and learning capacity.",
    dosages: ['Gummy', 'Powder', 'Tablet'],
    targets: ['School-Age Children', 'Picky Eaters', 'Parent Selection'],
    color: 'bg-lime-50 border-lime-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=children%20health%20supplement%20gummy%20vitamins%20calcium%20DHA%20colorful%20products%20on%20white%20surface%2C%20cute%20playful%20health%20product%20photography%20bright%20cheerful%20tones&width=400&height=260&seq=hf-11&orientation=landscape',
  },
  {
    id: 'slimming',
    icon: 'ri-scales-2-line',
    title: 'Weight Management Formula',
    tag: 'Weight Management',
    desc: 'Garcinia cambogia, white kidney bean and capsaicin complex with dietary fiber to support weight management and a lighter physique.',
    dosages: ['Capsule', 'Powder Sachet'],
    targets: ['Weight Management', 'Low-Calorie Diets', 'Slimming Programs'],
    color: 'bg-teal-50 border-teal-200',
    imgSrc: 'https://readdy.ai/api/search-image?query=weight%20management%20diet%20supplement%20with%20garcinia%20cambogia%20green%20tea%20extract%20capsules%20on%20white%20surface%2C%20slim%20figure%20health%20product%20minimal%20clean%20photography&width=400&height=260&seq=hf-12&orientation=landscape',
  },
];

function HotFormulasPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[380px] md:min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=colorful%20variety%20of%20health%20supplement%20products%20capsules%20powders%20gummies%20arranged%20beautifully%20on%20clean%20white%20marble%20surface%2C%20best%20selling%20nutraceutical%20product%20collection%20flat%20lay%20photography&width=1920&height=440&seq=hhf-hero-bg-01&orientation=landscape"
            alt="Best-Selling Formulas Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2310]/90 via-[#0d2310]/60 to-[#0d2310]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[{ label: 'Home', href: '/en' }, { label: 'Best-Selling Formulas' }]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              Home · Best-Selling Formulas
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              Best-Selling Formulas
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              Based on market demand, consumer demographics and channel positioning — helping brands choose the right health supplement formula direction.
            </p>
          </div>
        </div>
      </section>

      {/* Intro bar */}
      <section className="bg-white border-b border-gray-100 py-5">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'ri-medal-line', text: 'Market-Proven Formulas' },
              { icon: 'ri-flask-line', text: 'Diverse R&D Capabilities' },
              { icon: 'ri-leaf-line', text: 'Premium Natural Ingredients' },
              { icon: 'ri-settings-line', text: 'Flexible Customization' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg border border-green-100 flex-shrink-0">
                  <i className={`${item.icon} text-[#4CAF50] text-base`} />
                </div>
                <span className="text-[#1a2e1a] text-[0.875rem] font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formula cards */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              12 Best-Selling Directions
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              The Most Popular Formula Choices in the Market
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              Every formula direction is based on market demand analysis, combined with natural ingredient research, helping you enter the most in-demand categories with ease
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {formulasEn.map((f) => (
              <div
                key={f.id}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-green-200 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-full overflow-hidden" style={{ height: '170px' }}>
                  <img
                    src={f.imgSrc}
                    alt={f.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg border border-green-100">
                        <i className={`${f.icon} text-[#4CAF50] text-sm`} />
                      </div>
                      <h3 className="text-[#1a2e1a] font-bold text-[1rem]">{f.title}</h3>
                    </div>
                    <span className="text-[0.6875rem] text-[#4CAF50] bg-green-50 border border-green-100 px-2 py-0.5 rounded-full whitespace-nowrap">{f.tag}</span>
                  </div>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.7] mb-4">{f.desc}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {f.dosages.map((d) => (
                      <span key={d} className="text-[0.75rem] text-gray-500 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full">{d}</span>
                    ))}
                  </div>
                  <button
                    onClick={() => setExpandedId(expandedId === f.id ? null : f.id)}
                    className="w-full flex items-center justify-between text-[0.875rem] text-[#4CAF50] font-medium cursor-pointer hover:text-[#43a047] transition-colors py-1"
                  >
                    <span>{expandedId === f.id ? 'Collapse' : 'Learn More'}</span>
                    <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${expandedId === f.id ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedId === f.id && (
                    <div className="pt-3 border-t border-gray-100 mt-3">
                      <div className="text-[0.8125rem] text-gray-500 mb-2 font-medium">Target Audience:</div>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {f.targets.map((t) => (
                          <span key={t} className="text-[0.75rem] text-gray-600 bg-[#f7faf7] border border-gray-200 px-2 py-0.5 rounded-full">{t}</span>
                        ))}
                      </div>
                      <Link
                        to="/en/services/hot-formulas"
                        className="w-full flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white text-[0.875rem] font-semibold py-2.5 rounded-xl transition-all duration-300 cursor-pointer"
                      >
                        View Full Formula Details
                        <i className="ri-arrow-right-line text-sm" />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customization notice */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="bg-[#f7faf7] rounded-2xl p-8 md:p-12 border border-green-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                  Formula Customization
                </span>
                <h3 className="text-[#1a2e1a] font-bold text-[1.5rem] md:text-[1.875rem] mb-4">
                  These Are Reference Directions — Full Customization Available
                </h3>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.85]">
                  Every brand has a unique market position. Kang Yong Biotech provides formula customization services, tailoring the best formula combination based on your brand strategy, target audience and budget.
                </p>
              </div>
              <div className="space-y-3">
                {[
                  { icon: 'ri-check-line', text: 'Ingredient Substitution and Dosage Adjustment' },
                  { icon: 'ri-check-line', text: 'Flexible Dosage Form Switching' },
                  { icon: 'ri-check-line', text: 'Channel-Specific Specification Requirements' },
                  { icon: 'ri-check-line', text: 'Pre-Launch Regulatory Compliance Review' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-[#4CAF50] rounded-full flex-shrink-0">
                      <i className={`${item.icon} text-white text-xs`} />
                    </div>
                    <span className="text-[#1a2e1a] text-[1rem]">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            Found Your Formula Direction?
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            Connect with Kang Yong Biotech&apos;s formula consultants today and let us help you build the most popular health brand in the market.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/en/contact/form"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              Inquire About Best-Selling Formulas
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/en/services/hot-formulas"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              View More Formula Details
            </Link>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: 'Hot Formulas (Services)', desc: 'View detailed descriptions and dosage form options for all 12 best-selling formulas.', href: '/en/services/hot-formulas' },
        { title: 'All Dosage Forms', desc: 'Capsules, tablets, powders, jelly strips — choose the right form for your brand positioning.', href: '/en/services/dosage-forms' },
        { title: 'Inquire About Formula Development', desc: 'Fill out the inquiry form and let our R&D team help you create a bestselling product.', href: '/en/contact/form' },
      ]} />
    </>
  );
}

// ─── Route Dispatcher ─────────────────────────────────────────────────────────

export default function EnHomeSubPage() {
  const location = useLocation();

  const seoMap: Record<string, { title: string; description: string; keywords: string }> = {
    '/en/home/core': {
      title: 'Core Philosophy | Natural Ingredients & Brand Partnership | Kang Yong Biotech',
      description: "Kang Yong Biotech's core philosophy: returning to nature, strict natural ingredient traceability, consultative partnership approach helping brands build products with lasting market value.",
      keywords: 'Kang Yong core philosophy, natural ingredients, brand partnership, health supplement values',
    },
    '/en/home/advantages': {
      title: 'Kang Yong Advantages | Natural Ingredients, Integrated Service & Low MOQ',
      description: 'Four core advantages of Kang Yong Biotech: natural ingredient commitment, triple-win partnerships, integrated services, and low MOQ support — helping brands launch with lower risk.',
      keywords: 'Kang Yong advantages, low MOQ, natural ingredients, integrated OEM service, supplement manufacturing',
    },
    '/en/home/experience': {
      title: 'Service Experience | 250+ Brand Partners | Kang Yong Biotech',
      description: 'Kang Yong Biotech service experience: 250+ brand partners, 1600+ formula database, 70% repeat order rate, 20 years of health supplement manufacturing expertise.',
      keywords: 'Kang Yong experience, health supplement manufacturing, brand partners, OEM track record, repeat order rate',
    },
    '/en/home/hot-formulas': {
      title: 'Best-Selling Formulas | 12 Popular Health Supplement Directions | Kang Yong',
      description: 'Explore 12 best-selling formula directions: eye health, women wellness, men vitality, gut health, hydration beauty, hair health, baby, pet, fitness, sleep, kids growth, and weight management.',
      keywords: 'best-selling formulas, health supplement formulas, ODM formula, popular supplement directions, Kang Yong formulas',
    },
  };

  const seoData = seoMap[location.pathname] || seoMap['/en/home/core'];
  useSEO({ ...seoData, lang: 'en', canonicalPath: location.pathname });

  const renderPage = () => {
    switch (location.pathname) {
      case '/en/home/core':
        return <CorePage />;
      case '/en/home/advantages':
        return <AdvantagesPage />;
      case '/en/home/experience':
        return <ExperiencePage />;
      case '/en/home/hot-formulas':
        return <HotFormulasPage />;
      default:
        return (
          <section className="py-40 text-center">
            <h1 className="text-[#1a2e1a] font-bold text-[2rem]">Page Not Found</h1>
            <Link to="/en" className="mt-6 inline-block text-[#4CAF50] underline">Back to Home</Link>
          </section>
        );
    }
  };

  return <EnLayout>{renderPage()}</EnLayout>;
}
